import { HttpErrorResponse } from '@angular/common/http';

import { createAction, props } from '@ngrx/store';

import type { Position } from '@recruitee/http-types';
import type { OfferData, OfferFull, OfferStatus } from '@recruitee/offers-types';

import { OfferScope, UpdateSocialMediaActionPayload, StatusChangeType } from '../../types';

const namespace = 'Jobs';

const getActionType = (name: string) => `[${namespace}] ${name}`;

const actions = {
  follow: createAction(
    getActionType('follow'),
    props<{ payload: { id: number; scope: OfferScope } }>(),
  ),
  followSuccess: createAction(getActionType('followSuccess')),
  followFailed: createAction(
    getActionType('followFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse } }>(),
  ),
  unfollow: createAction(
    getActionType('unfollow'),
    props<{ payload: { id: number; scope: OfferScope } }>(),
  ),
  unfollowSuccess: createAction(getActionType('unfollowSuccess')),
  unfollowFailed: createAction(
    getActionType('unfollowFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse } }>(),
  ),
  duplicate: createAction(getActionType('duplicate'), props<{ payload: { id: number } }>()),
  duplicateSuccess: createAction(
    getActionType('duplicateSuccess'),
    props<{ payload: { offer: OfferFull } }>(),
  ),
  duplicateFailed: createAction(
    getActionType('duplicateFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse & { errorFields: any } } }>(),
  ),
  changeStatus: createAction(
    getActionType('changeStatus'),
    props<{ payload: { id: number; status: StatusChangeType; currentStatus?: OfferStatus } }>(),
  ),
  changeStatusSuccess: createAction(
    getActionType('changeStatusSuccess'),
    props<{ payload: { offer: OfferFull } }>(),
  ),
  changeStatusFailed: createAction(
    getActionType('changeStatusFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse & { errorFields: any } } }>(),
  ),
  create: createAction(getActionType('create'), props<{ payload: { offer: OfferData } }>()),
  createSuccess: createAction(
    getActionType('createSuccess'),
    props<{ payload: { data: OfferFull } }>(),
  ),
  createFailed: createAction(
    getActionType('createFailed'),
    props<{ payload: { error: HttpErrorResponse & { errorFields: any } } }>(),
  ),
  createFromRequisition: createAction(
    getActionType('createFromRequisition'),
    props<{ payload: { requisitionId: number } }>(),
  ),
  createFromRequisitionSuccess: createAction(
    getActionType('createFromRequisitionSuccess'),
    props<{ payload: { data: OfferFull } }>(),
  ),
  createFromRequisitionFailed: createAction(
    getActionType('createFromRequisitionFailed'),
    props<{ payload: { error: HttpErrorResponse & { errorFields: any } } }>(),
  ),
  update: createAction(
    getActionType('update'),
    props<{ payload: { offer: OfferData & { id: number } } }>(),
  ),
  updateSuccess: createAction(
    getActionType('updateSuccess'),
    props<{ payload: { data: OfferFull } }>(),
  ),
  updateFailed: createAction(
    getActionType('updateFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse & { errorFields: any } } }>(),
  ),
  removeOne: createAction(getActionType('removeOne'), props<{ payload: { id: number } }>()),
  removeOneSuccess: createAction(
    getActionType('removeOneSuccess'),
    props<{ payload: { id: number } }>(),
  ),
  removeOneFailed: createAction(
    getActionType('removeOneFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse } }>(),
  ),
  updatePositions: createAction(
    getActionType('updatePositions'),

    props<{ payload: Position[] }>(),
  ),
  updatePositionsSuccess: createAction(getActionType('updatePositionsSuccess')),
  updatePositionsFailed: createAction(
    getActionType('updatePositionsFailed'),
    props<{ payload: { error: HttpErrorResponse } }>(),
  ),
  preview: createAction(getActionType('preview'), props<{ payload: { offer: OfferData } }>()),
  previewSuccess: createAction(
    getActionType('previewSuccess'),
    props<{ payload: { previewUrl: string } }>(),
  ),
  previewFailed: createAction(
    getActionType('previewFailed'),
    props<{ payload: { error: HttpErrorResponse } }>(),
  ),
  updateSocialMedia: createAction(
    getActionType('updateSocialMedia'),
    props<{ payload: UpdateSocialMediaActionPayload }>(),
  ),
  updateSocialMediaSuccess: createAction(
    getActionType('updateSocialMediaSuccess'),
    props<{ payload: { offer: OfferFull } }>(),
  ),
  updateSocialMediaFailed: createAction(
    getActionType('updateSocialMediaFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse } }>(),
  ),
  updateSocialMediaImage: createAction(
    getActionType('updateSocialMediaImage'),
    props<{ payload: { id: number; file: File } }>(),
  ),
  updateSocialMediaImageSuccess: createAction(
    getActionType('updateSocialMediaImageSuccess'),
    props<{ payload: { id: number; url: string } }>(),
  ),
  updateSocialMediaImageFailed: createAction(
    getActionType('updateSocialMediaImageFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse } }>(),
  ),
  uploadCoverImage: createAction(
    getActionType('uploadCoverImage'),
    props<{ payload: { id: number; file: File } }>(),
  ),
  uploadCoverImageSuccess: createAction(
    getActionType('uploadCoverImageSuccess'),
    props<{ payload: { url: string } }>(),
  ),
  uploadCoverImageFailed: createAction(
    getActionType('uploadCoverImageFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse } }>(),
  ),
  updateSlug: createAction(
    getActionType('updateSlug'),
    props<{ payload: { id: number; slug: string } }>(),
  ),
  updateSlugSuccess: createAction(
    getActionType('updateSlugSuccess'),
    props<{ payload: { id: number; offer: OfferFull } }>(),
  ),
  updateSlugFailed: createAction(
    getActionType('updateSlugFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse } }>(),
  ),
};

export { actions as JobsActions };
